/**
 * See360 Analytics Tracker
 * Fires POST requests to the embed analytics endpoint for each user interaction.
 */

export interface AnalyticsConfig {
  apiUrl: string;
  apiKey: string;
  spin360Id: string;
  sessionId?: string;
}

type AnalyticsEventType = 'VIEW' | 'SPIN' | 'HOTSPOT_CLICK' | 'INTERIOR_OPEN' | 'EXTERIOR_OPEN' | 'FULLSCREEN' | 'ZOOM' | 'GALLERY_VIEW';

interface EventPayload {
  frameIndex?: number;
  hotspotId?: string;
  duration?: number;
}

export class Analytics {
  private config: AnalyticsConfig;
  private sessionId: string;
  private eventQueue: Array<{ event: AnalyticsEventType; payload: EventPayload; timestamp: number }> = [];
  private flushTimer: number | null = null;
  private spinStartTime: number | null = null;

  constructor(config: AnalyticsConfig) {
    this.config = config;
    this.sessionId = config.sessionId || this.generateSessionId();
  }

  private generateSessionId(): string {
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  /**
   * Track an analytics event. Events are batched and flushed periodically.
   */
  track(event: AnalyticsEventType, payload: EventPayload = {}): void {
    // Debounce SPIN events (only track every 500ms to avoid flooding)
    if (event === 'SPIN') {
      if (!this.spinStartTime) {
        this.spinStartTime = Date.now();
      }
      // Don't queue every spin, just update the duration
      return;
    }

    // If we were spinning, flush the spin event with duration
    if (this.spinStartTime) {
      this.eventQueue.push({
        event: 'SPIN',
        payload: { ...payload, duration: Date.now() - this.spinStartTime },
        timestamp: Date.now(),
      });
      this.spinStartTime = null;
    }

    this.eventQueue.push({ event, payload, timestamp: Date.now() });
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null) return;
    this.flushTimer = window.setTimeout(() => {
      this.flush();
      this.flushTimer = null;
    }, 2000); // Flush every 2 seconds
  }

  private async flush(): Promise<void> {
    if (this.eventQueue.length === 0 || !this.config.apiUrl) return;

    const events = [...this.eventQueue];
    this.eventQueue = [];

    for (const { event, payload } of events) {
      try {
        await fetch(`${this.config.apiUrl}/embed/analytics`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': this.config.apiKey,
          },
          body: JSON.stringify({
            spin360Id: this.config.spin360Id,
            sessionId: this.sessionId,
            event,
            frameIndex: payload.frameIndex,
            hotspotId: payload.hotspotId,
            duration: payload.duration,
            referrer: document.referrer || undefined,
          }),
        });
      } catch {
        // Silently fail — analytics should never break the viewer
      }
    }
  }

  /**
   * Force flush any remaining events (e.g., on page unload).
   */
  forceFlush(): void {
    if (this.spinStartTime) {
      this.eventQueue.push({
        event: 'SPIN',
        payload: { duration: Date.now() - this.spinStartTime },
        timestamp: Date.now(),
      });
      this.spinStartTime = null;
    }
    this.flush();
  }

  getSessionId(): string {
    return this.sessionId;
  }
}
